import {
  IconArrowUp,
  IconChartBar,
  IconFileText,
  IconInfoCircle,
  IconSettings,
  IconSparkles,
  IconTag,
  IconX,
} from "@tabler/icons-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/stores/auth";

const questions = [
  { key: "prompt1", icon: IconChartBar },
  { key: "prompt2", icon: IconTag },
  { key: "prompt3", icon: IconSettings },
  { key: "prompt4", icon: IconFileText },
] as const;

export function AssistantLauncher(): React.ReactElement {
  const { t } = useTranslation("budgetPlanning");
  const [question, setQuestion] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const shop = useAuthStore((state) => state.shops.find((item) => item.shopId === currentShopId));

  function sendQuestion(): void {
    if (!question.trim()) return;
    // The page provides the entry and composing interaction; there is no answer API in this delivery.
    toast.info(t("assistant.comingSoon"));
  }

  return (
    <Dialog>
      <DialogTrigger
        render={
          <Button
            size="icon"
            className="fixed right-7 bottom-7 z-40 size-12 rounded-full bg-ink text-white shadow-lg hover:bg-ink/90"
            aria-label={t("assistant.trigger")}
          />
        }
      >
        <IconSparkles className="size-5" stroke={1.75} aria-hidden />
      </DialogTrigger>
      <DialogPopup
        className="w-[880px] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl"
        showCloseButton={false}
        viewportClassName="grid-rows-[1fr_auto_1fr]"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-2.5 text-[15px] font-semibold">
            <span className="flex size-5 items-center justify-center">
              <IconSparkles className="size-5" stroke={1.75} aria-hidden />
            </span>
            {t("assistant.title")}
          </div>
          <DialogClose
            render={<Button variant="ghost" size="icon-sm" aria-label={t("assistant.close")} />}
          >
            <IconX className="size-4" stroke={1.75} aria-hidden />
          </DialogClose>
        </div>
        <div className="min-h-0 overflow-y-auto px-6 py-8 sm:px-12 sm:py-9">
          <DialogTitle className="text-[28px] leading-9 tracking-[-0.03em]">
            <span className="block">{t("assistant.welcome")}</span>
            <span>
              {t("assistant.identityPrefix")}
              <span className="text-indigo-9">{t("assistant.title")}</span>
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">{t("assistant.intro")}</DialogDescription>
          <div className="mt-7 rounded-xl border border-control-border bg-surface p-4 shadow-[0_0_0_3px_var(--color-subtle)] focus-within:border-ring">
            <textarea
              ref={inputRef}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendQuestion();
                }
              }}
              rows={2}
              aria-label={t("assistant.placeholder")}
              placeholder={t("assistant.placeholder")}
              className="w-full resize-none bg-transparent text-sm leading-6 outline-none placeholder:text-muted-foreground"
            />
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <IconInfoCircle className="size-3.5 shrink-0" stroke={1.75} aria-hidden />
                {t("assistant.composeHint")}
              </div>
              <Button
                size="icon"
                aria-label={t("assistant.send")}
                disabled={!question.trim()}
                onClick={sendQuestion}
              >
                <IconArrowUp className="size-4" stroke={1.75} aria-hidden />
              </Button>
            </div>
          </div>
          <div className="mt-7 space-y-2.5">
            <p className="pb-1 text-xs text-muted-foreground">{t("assistant.promptsTitle")}</p>
            {questions.map(({ key, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setQuestion(t(`assistant.${key}`));
                  inputRef.current?.focus();
                }}
                className="flex min-h-12 w-full items-center gap-3 rounded-lg border border-line px-3.5 text-left text-[13px] transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
                  <Icon className="size-[18px]" stroke={1.75} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">{t(`assistant.${key}`)}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-line bg-table-footer px-6 py-4 text-xs text-muted-foreground">
          <span className="truncate">{shop?.shopName ?? "AdPilot"}</span>
          <span className="hidden sm:inline">{t("assistant.inputHint")}</span>
        </div>
      </DialogPopup>
    </Dialog>
  );
}
